import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import moduleTemplatesService, {
    type ModuleTemplateDatum,
} from '@/services/moduleTemplates/moduleTemplatesService';
import { getModuleIcon } from '@/pages/ModuleTemplatesPage/iconHelper';
import { Check, Info } from 'lucide-react';

export interface SidebarModulesSelectorProps {
    value?: string[];
    onChange: (moduleIds: string[]) => void;
    disabled?: boolean;
    autoSelectDefaults?: boolean;
}

const SidebarModulesSelector: React.FC<SidebarModulesSelectorProps> = ({
    value = [],
    onChange,
    disabled = false,
    autoSelectDefaults = false,
}) => {
    // Fetch all active module templates
    const { data: templatesRes, isLoading } = useQuery({
        queryKey: ['module-templates-all-active'],
        queryFn: () =>
            moduleTemplatesService.getModuleTemplates(
                1,
                100,
                undefined,
                true,
                undefined,
                true,
            ),
        staleTime: 5 * 60 * 1000,
    });

    const activeTemplates = useMemo<ModuleTemplateDatum[]>(
        () => templatesRes?.data?.data ?? [],
        [templatesRes],
    );

    // Auto-select defaults on initial load if value is empty and autoSelectDefaults is true
    useEffect(() => {
        if (autoSelectDefaults && activeTemplates.length > 0 && (!value || value.length === 0)) {
            // Select all non-cohort-specific templates by default
            const defaultIds = activeTemplates
                .filter(t => !t.is_cohort_specific)
                .map(t => t.id);
            onChange(defaultIds);
        }
    }, [autoSelectDefaults, activeTemplates, value, onChange]);

    const handleToggle = (id: string) => {
        if (disabled) return;
        const current = value || [];
        if (current.includes(id)) {
            onChange(current.filter(item => item !== id));
        } else {
            onChange([...current, id]);
        }
    };

    const handleSelectAll = () => {
        if (disabled) return;
        onChange(activeTemplates.map(t => t.id));
    };

    const handleSelectDefaultsOnly = () => {
        if (disabled) return;
        onChange(activeTemplates.filter(t => !t.is_cohort_specific).map(t => t.id));
    };

    const handleClearAll = () => {
        if (disabled) return;
        onChange([]);
    };

    if (isLoading) {
        return (
            <div className="p-6 text-center text-xs text-gray-500 bg-gray-50/50 rounded-xl border border-gray-200">
                Loading available module templates...
            </div>
        );
    }

    if (activeTemplates.length === 0) {
        return (
            <div className="p-4 text-center text-xs text-gray-500 bg-gray-50/50 rounded-xl border border-gray-200">
                No active module templates found. Configure templates in Admin Masters &gt; Module Templates.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Quick selection toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                    Selected: <strong className="text-gray-900">{value?.length || 0}</strong> of{' '}
                    {activeTemplates.length} modules enabled for this cohort
                </span>
                {!disabled && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSelectDefaultsOnly}
                            className="text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                        >
                            Select Standard Defaults
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className="text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                        >
                            Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className="text-[11px] font-semibold text-red-500 hover:underline cursor-pointer"
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* Modules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeTemplates.map(template => {
                    const isSelected = value?.includes(template.id);
                    const IconComp = getModuleIcon(template.icon);

                    return (
                        <div
                            key={template.id}
                            onClick={() => handleToggle(template.id)}
                            className={`relative flex items-start gap-3 p-3.5 rounded-xl border transition-all cursor-pointer select-none ${
                                isSelected
                                    ? 'bg-blue-50/60 border-primary/40 shadow-xs'
                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/40'
                            } ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {/* Checkbox Box */}
                            <div
                                className={`w-5 h-5 rounded-md border mt-0.5 flex items-center justify-center shrink-0 transition-colors ${
                                    isSelected
                                        ? 'bg-primary border-primary text-white'
                                        : 'border-gray-300 bg-white'
                                }`}
                            >
                                {isSelected && <Check size={13} strokeWidth={3} />}
                            </div>

                            {/* Icon Box */}
                            <div
                                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${
                                    isSelected
                                        ? 'bg-white text-primary border-primary/20 shadow-xs'
                                        : 'bg-gray-50 text-gray-500 border-gray-200'
                                }`}
                            >
                                <IconComp size={18} />
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-1 mb-0.5">
                                    <span className="font-bold text-xs text-gray-900 truncate">
                                        {template.title}
                                    </span>
                                    <span className="font-bold text-[10px] text-gray-400">
                                        #{template.order_no}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                    <span className="font-mono text-[10px] text-gray-500 bg-white border border-gray-200 px-1 py-0.2 rounded">
                                        {template.slug}
                                    </span>
                                    {template.is_cohort_specific && (
                                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded-full">
                                            Cohort Specific
                                        </span>
                                    )}
                                </div>

                                {template.description && (
                                    <p className="text-[11px] text-gray-500 line-clamp-2 leading-tight">
                                        {template.description}
                                    </p>
                                )}

                                {template.cohort_notes && template.is_cohort_specific && (
                                    <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-800 bg-amber-50/70 p-1 rounded">
                                        <Info size={11} className="shrink-0 mt-0.5" />
                                        <span className="line-clamp-2">{template.cohort_notes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SidebarModulesSelector;
